import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RootRedirect from './components/RootRedirect';
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import ProjectManagement from './pages/ProjectManagement';
import TaskManagement from './pages/TaskManagement';
import TimeTransactions from './pages/TimeTransactions';
import BreakAndLeisure from './pages/BreakAndLeisure';
import ServiceManagement from './pages/ServiceManagement';
import RoleManagement from './pages/RoleManagement';
import QCReview from './pages/QCReview';
import Approvals from './pages/Approvals';
import { useMediaQuery } from '@mui/material';

function App() {
  const [mode, setMode] = useState('light');
  const prefersHighContrast = useMediaQuery('(prefers-contrast: more)');
  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  const theme = useMemo(
    () =>
      createTheme({
        spacing: 4,
        palette: {
          mode,
          primary: { main: '#3b82f6' },
          secondary: { main: '#8b5cf6' },
          background: {
            default: mode === 'light' ? '#fafbfc' : '#17181c',
            paper: mode === 'light' ? '#ffffff' : '#1f2024',
          },
          text: {
            primary: mode === 'light' ? '#111827' : '#f3f4f6',
            secondary: mode === 'light' ? '#6b7280' : 'rgba(255,255,255,0.65)',
          },
          divider: mode === 'light' ? '#e5e7eb' : 'rgba(255,255,255,0.08)',
          ...(prefersHighContrast && {
            primary: { main: '#ffffff' },
            secondary: { main: '#ffffff' },
            background: { default: '#000000' },
            text: { primary: '#ffffff' },
          }),
        },
        shape: { borderRadius: 10 },
        typography: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Inter", sans-serif',
          fontSize: 15,
          fontWeightLight: 300,
          fontWeightRegular: 400,
          fontWeightMedium: 500,
          fontWeightBold: 600,
          h1: { 
            fontWeight: 700, 
            fontSize: '2.5rem', 
            lineHeight: 1.2, 
            letterSpacing: '-0.5px' 
          },
          h2: { 
            fontWeight: 700, 
            fontSize: '2rem', 
            lineHeight: 1.3, 
            letterSpacing: '-0.3px' 
          },
          h3: { 
            fontWeight: 600, 
            fontSize: '1.75rem', 
            lineHeight: 1.3, 
            letterSpacing: '-0.2px' 
          },
          h4: { 
            fontWeight: 600, 
            fontSize: '1.5rem', 
            lineHeight: 1.4, 
            letterSpacing: '-0.1px' 
          },
          h5: { 
            fontWeight: 600, 
            fontSize: '1.25rem', 
            lineHeight: 1.4, 
            letterSpacing: '0px' 
          },
          h6: { 
            fontWeight: 600, 
            fontSize: '1.125rem', 
            lineHeight: 1.5, 
            letterSpacing: '0.15px' 
          },
          body1: { 
            fontSize: '0.9375rem', 
            lineHeight: 1.6, 
            letterSpacing: '0.15px',
            fontWeight: 400
          },
          body2: { 
            fontSize: '0.875rem', 
            lineHeight: 1.5, 
            letterSpacing: '0.15px',
            fontWeight: 400
          },
          button: { 
            textTransform: 'none', 
            fontWeight: 500,
            letterSpacing: '0.4px',
            fontSize: '0.9375rem'
          },
          caption: {
            fontSize: '0.8125rem',
            lineHeight: 1.4,
            letterSpacing: '0.4px',
            fontWeight: 400
          },
          overline: {
            fontSize: '0.75rem',
            lineHeight: 1.4,
            letterSpacing: '1px',
            fontWeight: 500,
            textTransform: 'uppercase'
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: mode === 'light' ? '#fafbfc' : '#17181c',
                color: mode === 'light' ? '#111827' : '#f3f4f6',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                boxShadow: mode === 'light' ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
                border: mode === 'light' ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.08)',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none', // remove default dark-mode tint overlay
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
                fontWeight: 500,
              },
              containedPrimary: {
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                backgroundColor: mode === 'light' ? '#ffffff' : 'rgba(255,255,255,0.04)',
                fontSize: '0.9375rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#e5e7eb' : 'rgba(255,255,255,0.12)',
                  transition: 'border-color 0.15s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#cbd5e0' : 'rgba(255,255,255,0.22)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 1,
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: '0.875rem',
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-head': {
                  backgroundColor: mode === 'light' ? '#f9fafb' : 'rgba(255,255,255,0.03)',
                  color: mode === 'light' ? '#6b7280' : 'rgba(255,255,255,0.7)',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: mode === 'light' ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.08)',
                },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                borderBottom: mode === 'light' ? '1px solid #f1f3f5' : '1px solid rgba(255,255,255,0.06)',
                fontSize: '0.875rem',
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                boxShadow: 'none',
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 6,
                fontWeight: 500,
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: 10,
              },
            },
          },
        },
      }),
    [mode, prefersHighContrast]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/" element={<RootRedirect />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout mode={mode} toggleMode={toggleMode}>
                    <Routes>
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_leader', 'team_member', 'view_only', 'cmo', 'art', 'copy', 'research']}>
                            <Dashboard />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/users" 
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_leader', 'team_member', 'view_only', 'cmo', 'partner', 'art', 'copy', 'research']}>
                            <UserManagement />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/projects" 
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_leader', 'team_member', 'view_only', 'cmo', 'partner', 'art', 'copy', 'research']}>
                            <ProjectManagement />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/tasks" 
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_member', 'team_leader', 'view_only', 'cmo', 'partner', 'art', 'copy', 'research']}>
                            <TaskManagement />
                          </ProtectedRoute>
                        } 
                      />
                      <Route
                        path="/time-transactions"
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_member', 'team_leader', 'view_only', 'cmo', 'partner', 'art', 'copy', 'research']}>
                            <TimeTransactions />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/break-leisure"
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'project_manager', 'team_member', 'team_leader', 'view_only', 'cmo', 'partner', 'art', 'copy', 'research']}>
                            <BreakAndLeisure />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/service-management"
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'cmo']}>
                            <ServiceManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/role-management"
                        element={
                          <ProtectedRoute requiredRoles={['admin']}>
                            <RoleManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/qc-review"
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'QC', 'cmo']}>
                            <QCReview />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/approvals"
                        element={
                          <ProtectedRoute requiredRoles={['admin', 'project_manager', 'team_leader', 'cmo']}>
                            <Approvals />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
